import React, { useState } from 'react';

// Material-UI
import { styled, useTheme } from '@mui/material/styles';
import {
    Typography,
    Button,
    Box,
    Switch,
    FormControlLabel,
    Radio,
    Checkbox,
    RadioGroup,
    InputAdornment,
    Grid,
} from '@mui/material';
import CasinoIcon from '@mui/icons-material/Casino';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { Alert } from '@mui/material';

// Components
import Tooltip from 'components/popups/Tooltip';
import {
    Section,
    Header,
    Content,
    OptionPickerItem,
} from 'components/ui/forms';
import { DeleteButton } from 'components/ui/buttons';
import { SubmitButton } from 'components/ui/buttons';
import { CancelButton } from 'components/ui/buttons';
import { TextField, DatePicker, TimePicker } from 'components/ui/inputs';

// Utility
import { useTextInput, useNumberInput, useToggle, useDatePicker } from 'hooks/forms';
import { generateCode } from 'utility/generate';
import { getMomentFromDateAndTime } from 'utility/validation';
import { useNotification } from 'context/notification';
import { formatPrice } from 'utility/numbers';

/**
 * Base form component
 * Can be used for creating or editing discount codes
 * Handles all form logic including validation
 */

export default function DiscountForm(props) {
    // *** Props *** //

    const {
        editMode,
        defaults,
        createdAtMsg,
        updatedAtMsg,
        usedCodes,
        onSubmit,
        onCancel,
        loading,
        used,
        onDelete,
    } = props;

    // *** Form Values *** //

    // Discount Code
    const code = useTextInput({ defaultValue: defaults?.code ?? '', ...codeConfig });

    // Discount Value
    const [type, setType] = useState(defaults?.discountFlat ? '$' : '%');
    const amount = useNumberInput({
        defaultValue: defaults?.discountFlat || defaults?.discountPercent || '',
        ...amountConfig,
    });

    // Usage Limits
    const usageLimitEnabled = useToggle({ defaultValue: Boolean(defaults?.limit) });
    const usageLimit = useNumberInput({ defaultValue: defaults?.limit ?? '', ...limitConfig });
    const userLimitEnabled = useToggle({ defaultValue: Boolean(defaults?.userLimit) });
    const userLimit = useNumberInput({ defaultValue: defaults?.userLimit ?? '', ...limitConfig });
    const scheduleEnabled = useToggle({ defaultValue: defaults?.scheduleEnabled });
    const startDate = useDatePicker({ defaultValue: defaults?.startDate });
    const startTime = useDatePicker({ defaultValue: defaults?.startDate });
    const endDate = useDatePicker({ defaultValue: defaults?.endDate });
    const endTime = useDatePicker({ defaultValue: defaults?.endDate });

    // Per Item Discounts Fields
    const perItemDiscount = useToggle({ defaultValue: Boolean(defaults?.perItemDiscount) });
    const productModeEnabled = useToggle({
        defaultValue: defaults?.products.some((p) => p.checked),
    });
    const [products, setProducts] = useState(defaults?.products);
    const productLimitEnabled = useToggle({ defaultValue: Boolean(defaults?.productLimit) });
    const productLimit = useNumberInput({
        defaultValue: defaults?.productLimit ?? '',
        ...limitConfig,
    });
    const ticketLimitEnabled = useToggle({ defaultValue: Boolean(defaults?.ticketLimit) });
    const ticketLimit = useNumberInput({
        defaultValue: defaults?.ticketLimit ?? '',
        ...limitConfig,
    });

    // Visibility
    const visible = useToggle({ defaultValue: defaults?.visible ?? true });

    // *** Errors *** //

    const [scheduleError, setScheduleError] = useState(null);
    const [productsError, setProductsError] = useState(null);

    // *** Hooks *** //

    const theme = useTheme();
    const { notify } = useNotification();

    // *** Handlers *** //

    const handleProductChange = (value) => {
        setProducts(value);
        setProductsError(null);
    };

    function handleSubmit() {
        let formError = false;

        if (code.value.length === 0) {
            code.setError('This field cannot be empty');
            formError = true;
        }

        if (usedCodes.includes(code.value.toLowerCase())) {
            code.setError('This code is already being used for this event');
            formError = true;
        }

        if (amount.value.length === 0) {
            amount.setError('Please enter a valid amount');
            formError = true;
        }

        if (type === '%' && Number(amount.value) > 100) {
            amount.setError("You can't set a discount greater than 100%");
            formError = true;
        }

        if (usageLimitEnabled.checked === true) {
            if (usageLimit.value === '' || usageLimit.value === '0') {
                usageLimit.setError('Please enter a valid limit');
                formError = true;
            }
        }

        if (userLimitEnabled.checked === true) {
            if (userLimit.value === '' || userLimit.value === '0') {
                userLimit.setError('Please enter a valid limit');
                formError = true;
            }
        }

        let scheduleStart = null;
        let scheduleEnd = null;

        if (scheduleEnabled.checked === true) {
            if (
                startDate.value === null ||
                startDate.value.isValid() === false ||
                startTime.value === null ||
                startTime.value.isValid() === false ||
                endDate.value === null ||
                endDate.value.isValid() === false ||
                endTime.value === null ||
                endTime.value.isValid() === false
            ) {
                formError = true;
                setScheduleError(null);
            } else {
                scheduleStart = getMomentFromDateAndTime(startDate.value, startTime.value);
                scheduleEnd = getMomentFromDateAndTime(endDate.value, endTime.value);

                if (scheduleStart >= scheduleEnd) {
                    formError = true;
                    setScheduleError('Start date must come before the end date.');
                } else {
                    setScheduleError(null);
                }
            }
        } else {
            setScheduleError(null);
        }

        let discountProducts = null;

        if (perItemDiscount.checked === true) {
            if (productModeEnabled.checked === true) {
                discountProducts = products.filter((p) => p.checked === true).map((p) => p.id);

                if (discountProducts.length === 0) {
                    setProductsError(
                        'You must select at least one ticket from the list. If you want this discount to apply to the whole order instead, select "Entire Order" above.',
                    );
                    formError = true;
                }
            }

            if (productLimitEnabled.checked === true) {
                if (productLimit.value === '' || productLimit.value === '0') {
                    productLimit.setError('Please enter a valid limit');
                    formError = true;
                }
            }

            if (ticketLimitEnabled.checked === true) {
                if (ticketLimit.value === '' || ticketLimit.value === '0') {
                    ticketLimit.setError('Please enter a valid ticket limit');
                    formError = true;
                }
            }
        }

        // Form contains invalid values. Don't submit.
        if (formError) {
            notify.warning('Please ensure all fields are filled out correctly');
            return;
        }

        // Form is good. Submit formData
        const formData = {
            code: code.value,
            discountPercent: type === '%' ? Number(amount.value) : null,
            discountFlat: type === '$' ? Number(amount.value) : null,
            limit: usageLimitEnabled.checked ? Number(usageLimit.value) : null,
            userLimit: userLimitEnabled.checked ? Number(userLimit.value) : null,
            startDate: scheduleStart?.format('YYYY-MM-DD HH:mm:00') || null,
            endDate: scheduleEnd?.format('YYYY-MM-DD HH:mm:59') || null,
            perItemDiscount: perItemDiscount.checked,
            products: discountProducts,
            productLimit:
                perItemDiscount.checked && productLimitEnabled.checked
                    ? Number(productLimit.value)
                    : null,
            ticketLimit:
                perItemDiscount.checked && ticketLimitEnabled.checked
                    ? Number(ticketLimit.value)
                    : null,
            visible: visible.checked,
        };
        onSubmit(formData);
    }

    const randomizeCode = () => {
        const result = generateCode(5);
        code.setValue(result);
    };

    // ** Discount Application Wording ** //

    let discountAmount = 'the discount';

    if (isNaN(amount.value) === false && Number(amount.value) !== 0) {
        if (type === '$') {
            discountAmount = formatPrice(amount.value);
        } else {
            discountAmount = amount.value + '%';
        }
    }

    let perOrderDescription = `Apply ${discountAmount} once to the total value of the order. (Recommended for most situations)`;
    let perTicketDescription = `Apply ${discountAmount} individually to every eligible ticket in the order.`;

    return (
        <Box display="flex" justifyContent="center" width="100%">
            <Box
                display="flex"
                flexDirection="column"
                flexGrow={1}
                p={[2, 4]}
                maxWidth={theme.breakpoints.values.md}
            >
                {used && (
                    <Box mb={4}>
                        <Alert severity="warning">
                            This discount code has been used to purchase tickets so some values can
                            no longer be edited. If you still need to change a locked value,
                            consider renaming this code and creating a new one with the original
                            name.
                        </Alert>
                    </Box>
                )}

                <Grid container spacing={4}>
                    <Grid item xs={12}>
                        <Section>
                            <Header>Discount Code</Header>
                            <Typography>
                                Enter a code purchasers will use during checkout to receive a
                                discount.
                            </Typography>

                            <Content>
                                <TextField
                                    {...code.formProps}
                                    variant="outlined"
                                    label="Discount Code"
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <Button
                                                    color="secondary"
                                                    startIcon={<CasinoIcon color="secondary" />}
                                                    onClick={randomizeCode}
                                                >
                                                    Random
                                                </Button>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Content>
                        </Section>
                    </Grid>

                    <Grid item xs={12}>
                        <Section>
                            <Header>Discount Value</Header>
                            <Typography>
                                Specify whether this is a percentage or a fixed dollar amount.
                            </Typography>

                            <Box mt={2}>
                                <RadioInput
                                    aria-label="type"
                                    row={true}
                                    value={type}
                                    onChange={(e) => setType(e.target.value)}
                                >
                                    <FormControlLabel
                                        value="%"
                                        disabled={used}
                                        control={<Radio />}
                                        label="Percentage"
                                    />
                                    <FormControlLabel
                                        value="$"
                                        disabled={used}
                                        control={<Radio />}
                                        label="Fixed Amount"
                                    />
                                </RadioInput>

                                <Box m={2} />

                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <TextField
                                            {...amount.formProps}
                                            disabled={used}
                                            label="Discount value"
                                            InputProps={{
                                                startAdornment:
                                                    type === '$' ? (
                                                        <InputAdornment position="start">
                                                            $
                                                        </InputAdornment>
                                                    ) : undefined,
                                                endAdornment:
                                                    type === '%' ? (
                                                        <InputAdornment position="end">
                                                            %
                                                        </InputAdornment>
                                                    ) : undefined,
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        </Section>
                    </Grid>

                    <Grid item xs={12}>
                        <Section>
                            <Header>Usage Limits</Header>
                            <Typography>
                                These limits apply to the discount code itself, no matter how it's
                                applied (Entire Order or Each Ticket). They control how often the
                                code can be used and by whom.
                            </Typography>

                            <Content>
                                <Box
                                    display="flex"
                                    flexDirection="column"
                                    alignItems="flex-start"
                                    mb={2}
                                >
                                    <FormControlLabel
                                        control={<Switch {...usageLimitEnabled.formProps} />}
                                        label="Limit Total Uses"
                                    />
                                    <SwitchSubHeader>
                                        Set the maximum number of times this code can be used across
                                        all orders.
                                    </SwitchSubHeader>
                                </Box>
                                {usageLimitEnabled.checked === true && (
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <Box ml="44px" mb={4}>
                                                <TextField
                                                    {...usageLimit.formProps}
                                                    disabled={!usageLimitEnabled.checked}
                                                    label={'Limit'}
                                                    error={
                                                        usageLimitEnabled.checked &&
                                                        usageLimit.formProps.error
                                                    }
                                                />
                                            </Box>
                                        </Grid>
                                    </Grid>
                                )}

                                <Box
                                    display="flex"
                                    flexDirection="column"
                                    alignItems="flex-start"
                                    mb={2}
                                >
                                    <FormControlLabel
                                        control={<Switch {...userLimitEnabled.formProps} />}
                                        label="Limit Per Customer"
                                    />
                                    <SwitchSubHeader>
                                        Restrict how many times a single customer can use this code.
                                        (This is tracked by their email address.)
                                    </SwitchSubHeader>
                                </Box>
                                {userLimitEnabled.checked === true && (
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <Box ml="44px">
                                                <TextField
                                                    {...userLimit.formProps}
                                                    disabled={!userLimitEnabled.checked}
                                                    label={'Limit'}
                                                    error={
                                                        userLimitEnabled.checked &&
                                                        userLimit.formProps.error
                                                    }
                                                />
                                            </Box>
                                        </Grid>
                                    </Grid>
                                )}
                            </Content>
                        </Section>
                    </Grid>

                    <Grid item xs={12}>
                        <Section>
                            <Header>Discount Schedule</Header>
                            <Typography>
                                Choose the date and time range when this discount code will be
                                valid.
                            </Typography>
                            <Content>
                                <Box
                                    display="flex"
                                    flexDirection="column"
                                    alignItems="flex-start"
                                    mb={2}
                                >
                                    <FormControlLabel
                                        control={<Switch {...scheduleEnabled.formProps} />}
                                        label="Enable Discount Schedule"
                                    />
                                </Box>
                                {scheduleEnabled.checked === true && (
                                    <Box ml="44px">
                                        <Grid container spacing={2}>
                                            <Grid item xs={6}>
                                                <DatePicker
                                                    {...startDate.formProps}
                                                    label="Start Date"
                                                    disabled={scheduleEnabled.checked === false}
                                                    error={
                                                        scheduleEnabled.checked &&
                                                        startDate.formProps.error
                                                    }
                                                />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <TimePicker
                                                    {...startTime.formProps}
                                                    label="Start Time"
                                                    disabled={scheduleEnabled.checked === false}
                                                    error={
                                                        scheduleEnabled.checked &&
                                                        startTime.formProps.error
                                                    }
                                                />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <DatePicker
                                                    {...endDate.formProps}
                                                    label="End Date"
                                                    disabled={scheduleEnabled.checked === false}
                                                    error={
                                                        scheduleEnabled.checked &&
                                                        endDate.formProps.error
                                                    }
                                                />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <TimePicker
                                                    {...endTime.formProps}
                                                    label="End Time"
                                                    disabled={scheduleEnabled.checked === false}
                                                    error={
                                                        scheduleEnabled.checked &&
                                                        endTime.formProps.error
                                                    }
                                                />
                                            </Grid>
                                        </Grid>

                                        {scheduleEnabled.checked && scheduleError ? (
                                            <Typography color="error">{scheduleError}</Typography>
                                        ) : null}
                                    </Box>
                                )}
                            </Content>
                        </Section>
                    </Grid>

                    <Grid item xs={12}>
                        <Section>
                            <Header>How the Discount is Applied</Header>
                            <Typography>
                                Choose how the discount is applied to a customer's order.
                            </Typography>
                            <Content>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <OptionPickerItem
                                            title="Entire Order"
                                            description={perOrderDescription}
                                            icon={ShoppingCartIcon}
                                            checked={perItemDiscount.checked === false}
                                            onClick={() => perItemDiscount.setValue(false)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <OptionPickerItem
                                            title="Each Ticket"
                                            description={perTicketDescription}
                                            icon={ConfirmationNumberIcon}
                                            checked={perItemDiscount.checked === true}
                                            onClick={() => perItemDiscount.setValue(true)}
                                        />
                                    </Grid>
                                </Grid>
                                {perItemDiscount.checked && (
                                    <Box mt={3}>
                                        <Box
                                            display="flex"
                                            flexDirection="column"
                                            alignItems="flex-start"
                                        >
                                            <FormControlLabel
                                                control={
                                                    <Switch {...productModeEnabled.formProps} />
                                                }
                                                label="Limit to Specific Tickets"
                                            />
                                            {productsError ? (
                                                <SwitchError>{productsError}</SwitchError>
                                            ) : (
                                                <SwitchSubHeader>
                                                    By default, the discount applies to all ticket
                                                    types. You can select specific ticket types if
                                                    you'd like to limit which ones are eligible.
                                                </SwitchSubHeader>
                                            )}
                                        </Box>

                                        {productModeEnabled.checked && products.length > 0 && (
                                            <Products
                                                products={products}
                                                onProductChange={handleProductChange}
                                                error={productsError}
                                            />
                                        )}

                                        <Box
                                            display="flex"
                                            flexDirection="column"
                                            alignItems="flex-start"
                                            mb={2}
                                            mt={4}
                                        >
                                            <FormControlLabel
                                                control={
                                                    <Switch {...productLimitEnabled.formProps} />
                                                }
                                                label="Limit Tickets per Order"
                                            />
                                            <SwitchSubHeader>
                                                Set the maximum number of tickets this discount can
                                                apply to within a single order. For percentage-based
                                                discounts, the code will apply to the cheapest
                                                eligible tickets first.
                                            </SwitchSubHeader>
                                        </Box>
                                        {productLimitEnabled.checked && (
                                            <Grid container spacing={2}>
                                                <Grid item xs={6}>
                                                    <Box ml="44px">
                                                        <TextField
                                                            {...productLimit.formProps}
                                                            label={'Limit'}
                                                            error={productLimit.formProps.error}
                                                        />
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        )}

                                        <Box
                                            display="flex"
                                            flexDirection="column"
                                            alignItems="flex-start"
                                            mb={2}
                                            mt={4}
                                        >
                                            <FormControlLabel
                                                control={
                                                    <Switch {...ticketLimitEnabled.formProps} />
                                                }
                                                label="Limit Total Tickets"
                                            />
                                            <SwitchSubHeader>
                                                Set the total number of tickets that can be
                                                discounted across all orders. Once this limit is
                                                reached, the discount code will no longer work.
                                            </SwitchSubHeader>
                                        </Box>
                                        {ticketLimitEnabled.checked === true && (
                                            <Grid container spacing={2}>
                                                <Grid item xs={6}>
                                                    <Box ml="44px">
                                                        <TextField
                                                            {...ticketLimit.formProps}
                                                            label={'Limit'}
                                                            error={ticketLimit.formProps.error}
                                                        />
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        )}
                                    </Box>
                                )}
                            </Content>
                        </Section>
                    </Grid>

                    <Grid item xs={12}>
                        <Section>
                            <Header>Discount Status</Header>
                            <Typography>
                                Use this toggle to activate or deactivate the discount code.
                            </Typography>

                            <Box
                                display="flex"
                                flexDirection="column"
                                alignItems="flex-start"
                                mt={2}
                            >
                                <FormControlLabel
                                    control={<Switch {...visible.formProps} />}
                                    label="Active"
                                />
                                <SwitchSubHeader>
                                    Toggle to make this discount code active.
                                </SwitchSubHeader>
                            </Box>
                        </Section>
                    </Grid>
                </Grid>

                <Box display="flex" justifyContent="flex-end" mt={6} mb={4}>
                    {editMode && (
                        <>
                            {used ? (
                                <Tooltip message="Discounts cannot be deleted after being used by a customer.">
                                    <span style={{ marginRight: 'auto' }}>
                                        <DeleteButton disabled>Delete</DeleteButton>
                                    </span>
                                </Tooltip>
                            ) : (
                                <DeleteButton sx={{ marginRight: 'auto' }} onClick={onDelete} disabled={loading}>
                                    Delete
                                </DeleteButton>
                            )}
                        </>
                    )}
                    <CancelButton onClick={onCancel} disabled={loading}>
                        Cancel
                    </CancelButton>
                    <SubmitButton onClick={handleSubmit} disabled={loading}>
                        Save
                    </SubmitButton>
                </Box>

                {createdAtMsg && (
                    <Typography align="right" variant="caption">
                        {createdAtMsg}
                    </Typography>
                )}

                {updatedAtMsg && (
                    <Typography align="right" variant="caption">
                        {updatedAtMsg}
                    </Typography>
                )}
            </Box>
        </Box>
    );
}

function Products(props) {
    const { products, onProductChange, disabled } = props;

    const handleChange = (event) => {
        const id = Number(event.target.id);
        const checked = event.target.checked;

        onProductChange(
            products.map((p) => {
                if (p.id === id) {
                    return { ...p, checked: checked };
                } else {
                    return { ...p };
                }
            }),
        );
    };

    return (
        <Box mt={2} ml="34px" display={'flex'} flexDirection={'column'} alignItems={'flex-start'}>
            {products.map((p) => (
                <Box key={p.id} display="flex" alignItems={'center'}>
                    <Checkbox
                        id={p.id}
                        checked={p.checked}
                        disabled={disabled}
                        onChange={handleChange}
                    />
                    {p.type === 1 ? <AdmissionIcon /> : <AddonIcon />}
                    <Typography>{p.name}</Typography>
                </Box>
            ))}
        </Box>
    );
}

const RadioInput = styled(RadioGroup)(({ theme }) => ({
    marginBottom: theme.spacing(1),
}));

const SwitchSubHeader = styled(Typography)(({ theme }) => ({
    marginLeft: 48,
    marginTop: -6,
    marginBottom: 8,
}));
SwitchSubHeader.defaultProps = { variant: 'caption' };

const SwitchError = styled(Typography)(({ theme }) => ({
    color: theme.palette.error.main,
    marginLeft: 48,
    marginTop: -6,
    marginBottom: 8,
}));
SwitchError.defaultProps = { variant: 'caption' };

const codeConfig = {
    noSpaces: true,
    maxLength: 50,
};
const amountConfig = {
    decimalPlaces: 2,
};
const limitConfig = {
    maxLength: 4,
    integerOnly: true,
};

const AdmissionIcon = styled(ConfirmationNumberIcon)(({ theme }) => ({
    height: '16px',
    width: '16px',
    marginLeft: 6,
    marginRight: 6,
}));

const AddonIcon = styled(AddShoppingCartIcon)(({ theme }) => ({
    height: '16px',
    width: '16px',
    marginLeft: 6,
    marginRight: 6,
}));
